import { trackEvent } from '@fs/zion-analytics'
import axios from '@fs/zion-axios'
import Cache from '@fs/zion-cache'
import CacheService from '@fs/zion-cache-service'
import { removeMemberFromGroup } from '@fs/zion-tree-groups'

const treeDataBase = '/service/tree/tree-data'

// Briefly cached, mainly to prevent multiple duplicate calls made between shared components that each need the data
const tenSecondLifeTime = 10000
export const cache = new Cache({
  storeName: 'group_management',
  type: 'memory',
  lifetime: tenSecondLifeTime,
})
const cacheServiceConfig = {
  // cache can be a Zion Cache config object or a Zion Cache Instance
  cache,
  strategy: 'cache-first', // default to `cache-first`
  queueConfig: { dedupe: () => {} }, // optional zion queue config object
}
export const cacheService = new CacheService(cacheServiceConfig)

/**
 * Gets a list of summaries (name, id, displayName) for each of the user's groups.
 * @throws 400 - The Group id wasn't valid.
 * @throws 403 - Only un-banished group members can view a group.
 * @throws 404 - Group not found.
 * @throws 410 - Group is tombstoned.
 * @returns {Promise} promise of cacheService.get
 */
export const getGroupsList = () => {
  return axios.get(`/service/tree/tree-data/groups/current-groups`)
}

/**
 * @description Fetch group limits from backend
 * @returns Promise with max admins per group and max groups per user
 */
export async function getGroupLimits() {
  return axios.get(`${treeDataBase}/groups/limits`).then((res) => res.data)
}

/**
 * @description Creates a new group.
 * @param {string} name - The group's name.
 * @param {string} description - The group's description.
 * @throws 400 - Name and joinPolicy must be set.
 * @throws 400 - Invalid character or too many characters in property
 * @throws 400 - User at maximum allowed group membership.
 */
export async function createGroup({ name, description, codeOfConduct, ordinanceSharing }) {
  const types = ['PRIVATE_PERSON_SHARING']
  if (ordinanceSharing) types.push('ORDINANCE_SHARING')
  const body = {
    codeOfConduct,
    description,
    joinPolicy: 'INVITE_ONLY', // Possible values: OPEN, REQUEST_TO_JOIN, INVITE_ONLY
    name,
    notifyOnJoin: true,
    notifyOnLeave: true,
    types,
  }
  cache.removeItem('group-list')
  return axios.post(`${treeDataBase}/groups`, body).then((res) => {
    trackEvent({ link_name: `service:CREATE_GROUP:SUCCESS` })
    return res
  })
}

/**
 * @description Gets a group's information.
 * @param {string} groupId - The group's ID.
 * @throws 400 - The group id was invalid.
 * @throws 403 - Only un-banished group members can view a group.
 * @throws 404 - Group not found.
 * @throws 410 - Group is tombstoned.
 */
export async function getGroup(groupId) {
  return axios.get(`${treeDataBase}/groups/${groupId}`).then((res) => res.data)
}

/**
 * @description Edit a group's properties.
 * @param {string} groupId - The group's ID.
 * @param {string} name - The group's name.
 * @param {string} description - The group's description.
 * @throws 400 - name cannot be empty.
 * @throws 400 - Invalid character or too many characters in a property.
 * @throws 403 - Only group admins can update group admin properties.
 * @throws 404 - Group not found.
 */
export async function editGroup({ groupId, name, description, codeOfConduct, ordinanceSharing, returnGroup = false }) {
  const types = ['PRIVATE_PERSON_SHARING']
  if (ordinanceSharing) types.push('ORDINANCE_SHARING')
  const queryParams = new URLSearchParams({ returnGroup }).toString()
  const searchQuery = queryParams ? `?${queryParams}` : ''
  const body = { codeOfConduct, description, name, types }
  return axios.post(`${treeDataBase}/groups/${groupId}${searchQuery}`, body)
}

// /**
//  * @description Gets a list of members in the group.
//  * @param {string} groupId - The group's ID.
//  * @throws 400 - The group id wasn't valid.
//  * @throws 403 - Only un-banished admins can view all members.
//  * @throws 404 - Group not found.
//  * @throws 410 - Group is tombstoned.
//  */
// export async function getGroupMembers(groupId) {
//   return axios.get(`${treeDataBase}/groups/${groupId}/members`).then((res) => {
//     const members = res.data.members.filter((member) => ['ADMIN', 'MEMBER'].includes(member.status))
//     const joinRequests = res.data.members.filter((member) => member.status === 'PENDING')
//     return { members, joinRequests }
//   })
// }

// JOIN REQUEST ENDPOINTS

/**
 * @description For an admin to accept or decline a join request.
 * @param {string} groupId - The group's ID.
 * @param {string} cisId - The user's ID.
 * @param {bool} approveJoinRequest - Whether admin approves or declines join request. Becomes either 'ACCEPTED' or 'DECLINED'
 * @throws 400 - Invalid group id
 * @throws 400 - Invalid state change - move to PENDING, already accepted, no state change, etc.
 * @throws 400 - Invalid character or too many characters in reason statement
 * @throws 400 - Requesting user is already a member of the group
 * @throws 400 - Unable to join group due to group or user limitations
 * @throws 403 - Requesting user is banished from the group
 * @throws 404 - Group not found
 * @throws 404 - Group join request not found
 * @throws 410 - Group is tombstoned
 */
export async function respondToJoinRequest(groupId, cisId, approveJoinRequest) {
  if (!approveJoinRequest) return removeMemberFromGroup(groupId, cisId)
  return axios.post(`${treeDataBase}/groups/${groupId}/members/by-id/${cisId}`, { status: 'MEMBER' })
}

// USER ENDPOINTS

/**
 * @description Set new image for a group.
 * @param {string} groupId - The group's ID.
 * @param {file} file - The group's new image.
 * @throws 400 - invalid groupId.
 * @throws 400 - invalid multipart form image.
 * @throws 403 - only group admins can update the group image.
 * @throws 404 - group not found.
 * @throws 410 - group is tombstoned.
 */
export function setGroupImage(groupId, file) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('height', 1)
  formData.append('width', 1)
  formData.append('x', 0)
  formData.append('y', 0)
  formData.append('rotation', 0)
  formData.append('mediaType', 'image/jpeg')
  return axios.post(`${treeDataBase}/groups/${groupId}/image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * @description Set new image for a group.
 * @param {string} groupId - The group's ID.
 * @param {file} file - The group's new image.
 * @throws 400 - invalid groupId.
 * @throws 403 - only group admins can delete the group image.
 * @throws 404 - group not found.
 */
export function removeGroupImage(groupId) {
  return axios.delete(`${treeDataBase}/groups/${groupId}/image`)
}

/**
 * @description Tombstones a group.
 * @param {string} groupId - Group ID to tombstone.
 */
export async function tombstoneGroup(groupId) {
  return axios.post(`/service/tree/tree-data/groups/${groupId}`, { tombstoned: true }).catch((error) => {
    throw error.response
  })
}
